import { API_BASE_URL } from './index';
import { getToken, getUser } from './auth';

const getAuthHeaders = () => {
  const token = getToken();
  const user = getUser();

  if (!user) {
    throw new Error('Usuario no autenticado');
  }

  return {
    'Content-Type': 'application/json',
    ...(token ? { 'X-Session-Token': token } : {}),
    // Needed for @require_permission(...) decorator on backend
    ...(user.id ? { 'X-Usuario-ID': user.id } : {}),
    ...(user.companyId ? { 'X-Company-ID': user.companyId } : {}),
  };
};

const getCompanyId = () => {
  const user = getUser();
  if (!user?.companyId) {
    throw new Error('Company ID no disponible');
  }
  return user.companyId;
};

export const userManagementApi = {
  getSucursales: async () => {
    const idcompany = getCompanyId();

    const response = await fetch(`${API_BASE_URL}/api/sucursales?idcompany=${encodeURIComponent(idcompany)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load sucursales' }));
      throw new Error(error.error || 'Failed to load sucursales');
    }

    return response.json();
  },

  getRoles: async () => {
    const idcompany = getCompanyId();

    const response = await fetch(`${API_BASE_URL}/api/roles?idcompany=${encodeURIComponent(idcompany)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load roles' }));
      throw new Error(error.error || 'Failed to load roles');
    }

    return response.json();
  },

  getPermisos: async () => {
    const response = await fetch(`${API_BASE_URL}/api/permisos`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load permissions' }));
      throw new Error(error.error || 'Failed to load permissions');
    }

    return response.json();
  },

  getUsuarios: async () => {
    const idcompany = getCompanyId();

    const response = await fetch(`${API_BASE_URL}/api/usuarios?idcompany=${encodeURIComponent(idcompany)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load users' }));
      throw new Error(error.error || 'Failed to load users');
    }

    return response.json();
  },

  createUsuario: async (data) => {
    const user = getUser();
    const created_by = user.id;
    const idcompany = user.companyId;
    const created_at = new Date().toISOString();

    const response = await fetch(`${API_BASE_URL}/api/usuarios`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        id: data.id,
        nombre: data.nombre,
        usuario: data.usuario,
        password: data.password,
        idsucursal: data.idsucursal,
        idrol: data.idrol,
        idcompany,
        created_at,
        created_by,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create user' }));
      throw new Error(error.error || 'Failed to create user');
    }

    return response.json();
  },

  updateUsuario: async (data) => {
    const user = getUser();
    const created_by = user.id;
    const idcompany = user.companyId;
    const created_at = new Date().toISOString();

    const response = await fetch(`${API_BASE_URL}/api/usuarios`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        id: data.id,
        nombre: data.nombre,
        usuario: data.usuario,
        idsucursal: data.idsucursal,
        idrol: data.idrol,
        idcompany,
        created_at,
        created_by,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update user' }));
      throw new Error(error.error || 'Failed to update user');
    }

    return response.json();
  },

  resetPassword: async ({ id, password }) => {
    const response = await fetch(`${API_BASE_URL}/api/usuarios/reset-password`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to reset password' }));
      throw new Error(error.error || 'Failed to reset password');
    }

    return response.json();
  },

  deleteUsuario: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/usuarios`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete user' }));
      throw new Error(error.error || 'Failed to delete user');
    }

    return response.json();
  },

  createRol: async (data) => {
    const user = getUser();
    const idcompany = user.companyId;
    const created_by = user.id;

    const response = await fetch(`${API_BASE_URL}/api/roles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        nombre: data.nombre,
        descripcion: data.descripcion,
        permisos: data.permisos,
        idcompany,
        created_by,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create role' }));
      throw new Error(error.error || 'Failed to create role');
    }

    return response.json();
  },

  updateRol: async (id, data) => {
    const response = await fetch(`${API_BASE_URL}/api/roles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        nombre: data.nombre,
        descripcion: data.descripcion,
        permisos: data.permisos,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update role' }));
      throw new Error(error.error || 'Failed to update role');
    }

    return response.json();
  },

  deleteRol: async (id) => {
    const response = await fetch(`${API_BASE_URL}/api/roles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to delete role' }));
      throw new Error(error.error || 'Failed to delete role');
    }

    return response.json();
  },
};

